import { queryOptions, useQuery } from '@tanstack/react-query';

import { TimeRange, ApiResponse, QueryOptions } from '@cast/shared-types';

import { apiClient } from '@cast/infrastructure-api';

const apiFn = apiClient.dbo.getCacheGroups;

export type CacheGroupsQueryData = {
  range: TimeRange;
  response: ApiResponse<typeof apiFn>;
};

export type CacheGroupsQueryArgs<SELECT> = QueryOptions<
  CacheGroupsQueryData,
  SELECT
> & {
  deployed?: boolean;
  key?: string;
  getRange: () => TimeRange;
};

const DBO_CACHE_GROUPS_QUERY_KEY = 'dbo-cache-groups';

export const getCacheGroupsQueryKey = ({
  deployed,
  key,
}: { deployed?: boolean; key?: string } = {}) => {
  if (deployed !== undefined || key !== undefined) {
    return [DBO_CACHE_GROUPS_QUERY_KEY, deployed, key] as const;
  }
  return [DBO_CACHE_GROUPS_QUERY_KEY] as const;
};

const getCacheGroupsQueryOptions = ({
  key,
  getRange,
  deployed,
}: {
  deployed?: boolean;
  key?: string;
  getRange: () => TimeRange;
}) =>
  queryOptions({
    queryKey: getCacheGroupsQueryKey({ deployed, key }),
    queryFn: async () => {
      const range = getRange();
      const { data } = await apiFn({
        metricsRangeStart: range.from,
        metricsRangeEnd: range.to,
        deployed,
      });
      return { range, response: data };
    },
  });

export const useCacheGroupsQuery = <SELECT = CacheGroupsQueryData>({
  key,
  getRange,
  deployed,
  select,
  refetchInterval,
}: CacheGroupsQueryArgs<SELECT>) => {
  return useQuery({
    ...getCacheGroupsQueryOptions({ key, getRange, deployed }),
    select,
    refetchInterval,
  });
};
